/**
 * Chinese New Year coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lunar_new_year`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const chineseNewYearColoringPagesPack: ToilePack = {
    slug: 'chinese-new-year-coloring-pages',
    category: 'season',
    name: 'Chinese New Year coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'dragon', label: 'Dragon', subject: 'a long Chinese dragon curling across the page, scales, whiskers and flame-shaped fins, chasing a round pearl'},
        {key: 'lanterns', label: 'Lanterns', subject: 'three round paper lanterns with tassels hanging from a plum branch, a string of firecrackers beside them'},
        {key: 'goat', label: 'Goat', subject: 'a smiling goat with curled horns holding a red envelope, plum blossoms and gold coins around its feet'},
    ],
    styles: [
        {key: 'bold', label: 'Bold outline', style: 'thick even bold black outline, large simple shapes with wide open areas for crayons, easy for young children', palette: 'black ink only', framing: 'The drawing fills the page and is centered with a white margin.'},
        {key: 'detailed', label: 'Detailed', style: 'fine uniform black line with intricate scale, fur and pattern work, many small closed areas for older children and adults', palette: 'black ink only', framing: 'The drawing is centered with a generous white margin.'},
        {key: 'border', label: 'Festive border', style: 'medium black line with the scene set inside a framed border of clouds, plum blossoms and coins', palette: 'black ink only', framing: 'The scene sits inside a ruled border, centered with a white margin around it.'},
    ],
    directions: ['rooster', 'horse year', 'zodiac animals'],
    directionHint: 'try: rooster',
    seo: {
        title: 'Chinese New Year Coloring Pages: Dragon, Lanterns, Goat',
        metaDescription: 'Printable Chinese New Year coloring pages: a dragon, paper lanterns and a goat, in bold, detailed and festive-border styles.',
        h1: 'Chinese New Year coloring pages: dragon, lanterns, goat',
        tagline: 'Nine black-and-white pages, three subjects at three levels of detail: bold outline, detailed, festive border.',
        intro: 'Printable coloring pages for the Lunar New Year, for children at home, in class or at a community celebration. Each is a clean black outline on white: a dragon chasing a pearl, three paper lanterns with firecrackers, or a goat holding a red envelope. Bold outlines suit young children, detailed line work suits older children and adults, and the festive border frames the scene. Type another subject, such as a rooster, and the styles carry it.',
        useCases: [
            'A rainy-day activity at home',
            'A classroom sheet for a New Year lesson',
            'A table activity at a community event',
            'A set for a school library display',
        ],
        faq: [
            {q: 'Can I print these at home?', a: 'Yes. Each page is a single black-and-white sheet on white, ready to print.'},
            {q: 'Which style is for which age?', a: 'Bold outline has thick, simple shapes with open areas for crayons. Detailed has fine lines and many small areas for older children and adults.'},
            {q: 'Are there any colours or grey tones?', a: 'No. The pages are black ink on white, with no shading, grey tones or fills.'},
            {q: 'Can I change the subject?', a: 'Yes. Type a subject such as a horse; the result may differ from the sample.'},
        ],
    },
}
